import { ErrorComponent, createRouter } from "@tanstack/react-router";
import { StandaloneNotFound } from "./components/not-found/StandaloneNotFound";
import { captureCaughtException } from "./lib/analytics";
import { authRoute } from "./pages/$authRoute";
import { layoutRoute } from "./pages/$layoutRoute";
import { notFoundRoute } from "./pages/$notFoundRoute";
import { rootRoute } from "./pages/$rootRoute";
import { searchProviderRoute } from "./pages/$searchProviderRoute";
import { accountAddressesRoute } from "./pages/account/account-addresses-page";
import { accountMessagesRoute } from "./pages/account/account-messages-page";
import { accountPreferencesRoute } from "./pages/account/account-preferences-page";
import { accountRoute } from "./pages/account/account-page";
import { accountProfileRoute } from "./pages/account/account-profile-page";
import { accountSavedListsRoute } from "./pages/account/account-saved-lists-page";
import { accountSecurityRoute } from "./pages/account/account-security-page";
import { accountStockRoute } from "./pages/account/account-stock-page";
import { accountSupplierMatchRoute } from "./pages/account/account-supplier-match-page";
import { activateRoute } from "./pages/activate/ActivatePage";
import { cartRoute } from "./pages/cart/CartPage";
import { checkoutRoute } from "./pages/checkout/CheckoutPage";
import { enterRoute } from "./pages/enter/EnterPage";
import { homeRoute } from "./pages/home/HomePage";
import { loginRoute } from "./pages/login/LoginPage";
import { logoutRoute } from "./pages/logout/LogoutPage";
import { onboardingRoute } from "./pages/onboarding/OnboardingPage";
import { orderConfirmationRoute } from "./pages/order-confirmation/OrderConfirmationPage";
import { ordersRoute } from "./pages/orders/OrdersPage";
import { resetPasswordRoute } from "./pages/reset-password/ResetPasswordPage";
import { searchInputRoute } from "./pages/search/SearchInputPage";
import { searchRoute } from "./pages/search/SearchPage";
import { supplierMatchConfirmRoute } from "./pages/supplier-match-confirm/SupplierMatchConfirmPage";
import { queryClient } from "./queries/queryClient";
// Exported so Storybook page stories can build a router over the *real* route
// tree with an in-memory history (see the page-story harness), rendering the
// actual shell + page rather than a stand-in.
export const routeTree = rootRoute.addChildren([
  //
  loginRoute,
  resetPasswordRoute,
  logoutRoute,
  // Public, like its siblings above: `/enter` exists to mint the session
  // `authRoute` demands, so it cannot live behind that gate.
  enterRoute,
  // Public for the same reason as `/enter`: the confirmation-link click is
  // what proves mailbox ownership, and the person clicking may not be logged
  // in as this salon at all - gating it behind login would break that.
  supplierMatchConfirmRoute,
  authRoute.addChildren([
    // Outside the app shell, behind the session gate. Both are exempt from the
    // first-login gate in `authRoute` (see `lib/first-login/gate`).
    activateRoute,
    onboardingRoute,
    searchProviderRoute.addChildren([
      //
      layoutRoute.addChildren([
        //
        homeRoute,
        cartRoute,
        checkoutRoute,
        accountRoute.addChildren([
          accountProfileRoute,
          accountAddressesRoute,
          accountSupplierMatchRoute,
          accountMessagesRoute,
          accountSecurityRoute,
          accountPreferencesRoute
        ]),
        accountSavedListsRoute,
        accountStockRoute,
        ordersRoute,
        orderConfirmationRoute,
        searchRoute,
        // Last, and outranked by every route above: only receives what none of
        // them matched.
        notFoundRoute
      ]),
      // Outside `layoutRoute` on purpose: phone search is a full-screen page
      // with no app shell around it. See `SearchInputPage`.
      searchInputRoute
    ])
  ])
]);

/**
 * The router only mounts a catch boundary around a route when it has an error
 * component, and only a mounted boundary calls `defaultOnCatch`. Naming the
 * library's own default keeps the fallback UI as it was while making loader and
 * render errors reportable (KAN-248). Exported so a test can mount these exact
 * options on a router whose routes throw.
 */
export const routerErrorReporting = {
  defaultErrorComponent: ErrorComponent,
  defaultOnCatch: (error: Error) =>
    captureCaughtException(error, { boundary: "router" })
};

export const router = createRouter({
  routeTree,
  context: {
    queryClient
  },
  // Last-resort fallback for a not-found nothing else handled. An unknown URL
  // is normally claimed by the `$` route inside the shell (`notFoundRoute`), so
  // this is what is left: it assumes no session, shell or data.
  defaultNotFoundComponent: StandaloneNotFound,
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
  ...routerErrorReporting
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
